/**
 * AtcManifestService - Thin facade / orchestrator for ATC manifest operations
 *
 * Delegates to:
 * - AtcManifestMemoryCache  — Tier 1 in-memory cache
 * - AtcManifestCacheService — Tier 2 localStorage cache
 * - AtcManifestFetchService — Tier 3 network fetch (circuit-breaker + validation)
 * - AtcManifestSwrController — SWR background-refresh orchestration
 * - mappers/AtcManifestMapper — API → domain data transformation
 */

import { Logger } from '@/app/utils/logger';
import { DataFetchLogger, type DataSource } from '@/app/services/logging/DataFetchLogger';
import { AtcManifestCacheService } from './AtcManifestCacheService';
import { AtcManifestMemoryCache } from './AtcManifestMemoryCache';
import {
  AtcManifestSwrController,
  type BackgroundRefreshCallback,
} from './AtcManifestSwrController';
import {
  fetchMainManifestFromNetwork,
  fetchStationManifestFromNetwork,
  getManifestCircuitBreaker,
  getStationCircuitBreaker,
} from './AtcManifestFetchService';
import {
  AtcMainManifest,
  AtcStationManifest,
  AtcStation,
  AtcStationInfo,
} from '@/types/AtcManifest';
import { AtcManifestResult } from '@/types/AtcCache';
import {
  mapApiMainManifestToStations,
  mapApiStationManifestToInternal,
  mergeStationWithManifest,
} from './mappers/AtcManifestMapper';

const LOG_TAG = 'atc-manifest';

// Re-export for backward compatibility
export type { BackgroundRefreshCallback };

// ── Helpers ──────────────────────────────────────────────────────

/** Log a cache-hit via DataFetchLogger. */
function logCacheHit(
  operation: string,
  source: DataSource,
  age: number,
  itemCount: number,
): void {
  DataFetchLogger.logFetch({
    operation,
    source,
    status: 'success',
    durationMs: 0,
    cacheHit: true,
    cacheAge: age,
    itemCount,
  });
}

export interface AtcManifestServiceApi {
  fetchMainManifest(forceRefresh?: boolean): Promise<AtcMainManifest>;
  fetchMainManifestWithSource(forceRefresh?: boolean): Promise<AtcManifestResult<AtcMainManifest>>;
  fetchStationManifest(
    stationInfo: AtcStationInfo | { manifestUrl: string; airportCode?: string; stationType?: string },
    forceRefresh?: boolean
  ): Promise<AtcStationManifest>;
  fetchStationManifestWithSource(
    stationInfo: AtcStationInfo | { manifestUrl: string; airportCode?: string; stationType?: string },
    forceRefresh?: boolean
  ): Promise<AtcManifestResult<AtcStationManifest>>;
  getAvailableStations(forceRefresh?: boolean): Promise<AtcStation[]>;
  loadStationScenarios(station: AtcStation, forceRefresh?: boolean): Promise<AtcStation>;
  getRandomScenarioUrl(station: AtcStation): Promise<string | null>;
  clearCache(): void;
  clearStationCache(manifestUrl: string): void;
}

/**
 * Service for fetching and managing ATC manifests from the CDN
 *
 * Uses a three-tier caching strategy:
 * 1. In-memory cache (fastest, session-only)
 * 2. localStorage cache via AtcManifestCacheService (persistent across sessions)
 * 3. Network fetch (fallback when caches miss or are stale)
 */
export class AtcManifestService {

  // ── SWR subscription passthrough ─────────────────────────────

  /**
   * Register a callback to be notified when stations are refreshed in the background
   * @returns Unsubscribe function
   */
  static onBackgroundRefresh(callback: BackgroundRefreshCallback): () => void {
    return AtcManifestSwrController.onBackgroundRefresh(callback);
  }

  // ── In-flight status passthrough ─────────────────────────────

  static isMainManifestRefreshInFlight(): boolean {
    return AtcManifestSwrController.isMainManifestRefreshInFlight();
  }

  static isStationManifestRefreshInFlight(manifestUrl: string): boolean {
    return AtcManifestSwrController.isStationManifestRefreshInFlight(manifestUrl);
  }

  // ── Main manifest ────────────────────────────────────────────

  /**
   * Fetch the main manifest from the CDN
   * Uses three-tier caching: memory → localStorage → network
   */
  static async fetchMainManifest(forceRefresh = false): Promise<AtcMainManifest> {
    const result = await this.fetchMainManifestWithSource(forceRefresh);
    return result.data;
  }

  /**
   * Fetch the main manifest with source information.
   * Returns the data along with cache source and staleness info.
   *
   * Resilience:
   * - Circuit breaker protection prevents cascading failures
   * - When circuit is open, returns cached data immediately
   */
  static async fetchMainManifestWithSource(forceRefresh = false): Promise<AtcManifestResult<AtcMainManifest>> {
    const circuitBreaker = getManifestCircuitBreaker();

    // Circuit-open fast-path: return localStorage cache without network
    if (circuitBreaker.isOpen() && !forceRefresh) {
      const localCached = AtcManifestCacheService.getCachedMainManifest();
      if (localCached) {
        const age = Date.now() - localCached.timestamp;
        Logger.info('Circuit open - returning cached main manifest without network request', LOG_TAG);
        logCacheHit('manifest:fetch', 'localStorage-cache', age, localCached.data.station_count);
        return { data: localCached.data, source: 'cache', isStale: true, cacheAge: age };
      }
      // Circuit is open but no cache — will try network anyway as last resort
    }

    // Tier 1: In-memory cache
    if (!forceRefresh) {
      const memoryCached = AtcManifestMemoryCache.getMainManifest();
      if (memoryCached) {
        const age = Date.now() - memoryCached.timestamp;
        logCacheHit('manifest:fetch', 'memory-cache', age, memoryCached.data.station_count);
        Logger.debug('Using in-memory cached main manifest', LOG_TAG);
        return { data: memoryCached.data, source: 'cache', isStale: false, cacheAge: age };
      }
    }

    // Tier 2: localStorage cache
    if (!forceRefresh) {
      const localCached = AtcManifestCacheService.getCachedMainManifest();
      if (localCached) {
        const age = Date.now() - localCached.timestamp;
        const cacheStatus = AtcManifestCacheService.getMainManifestCacheStatus();

        // Promote into memory tier
        AtcManifestMemoryCache.setMainManifest(localCached.data, localCached.timestamp);

        logCacheHit('manifest:fetch', 'localStorage-cache', age, localCached.data.station_count);
        Logger.debug(`Using localStorage cached main manifest (stale: ${cacheStatus.isStale})`, LOG_TAG);

        // SWR: if stale, schedule background refresh
        if (cacheStatus.isStale) {
          Logger.info('localStorage cache is stale, triggering deferred background refresh', LOG_TAG);
          AtcManifestSwrController.scheduleMainManifestRefresh(
            circuitBreaker.isOpen(),
            () => this.performMainManifestRefresh(),
          );
        }

        return { data: localCached.data, source: 'cache', isStale: cacheStatus.isStale, cacheAge: age };
      }
    }

    // Tier 3: Network fetch
    return this.fetchAndCacheMainManifest();
  }

  // ── Station manifest ─────────────────────────────────────────

  /**
   * Fetch a station's manifest from the CDN
   * Uses three-tier caching: memory → localStorage → network
   */
  static async fetchStationManifest(
    stationInfo: AtcStationInfo | { manifestUrl: string; airportCode?: string; stationType?: string },
    forceRefresh = false,
  ): Promise<AtcStationManifest> {
    const result = await this.fetchStationManifestWithSource(stationInfo, forceRefresh);
    return result.data;
  }

  /**
   * Fetch a station's manifest with source information.
   * Returns the data along with cache source and staleness info.
   */
  static async fetchStationManifestWithSource(
    stationInfo: AtcStationInfo | { manifestUrl: string; airportCode?: string; stationType?: string },
    forceRefresh = false,
  ): Promise<AtcManifestResult<AtcStationManifest>> {
    const manifestUrl = 'manifest_url' in stationInfo
      ? stationInfo.manifest_url
      : stationInfo.manifestUrl;

    const circuitBreaker = getStationCircuitBreaker();

    // Circuit-open fast-path
    if (circuitBreaker.isOpen() && !forceRefresh) {
      const localCached = AtcManifestCacheService.getCachedStationManifest(manifestUrl);
      if (localCached) {
        const age = Date.now() - localCached.timestamp;
        Logger.info(`Circuit open - returning cached station manifest for ${manifestUrl} without network request`, LOG_TAG);
        logCacheHit('stations:fetch', 'localStorage-cache', age, localCached.data.scenario_count);
        return { data: localCached.data, source: 'cache', isStale: true, cacheAge: age };
      }
    }

    // Tier 1: In-memory cache
    if (!forceRefresh) {
      const memoryCached = AtcManifestMemoryCache.getStationManifest(manifestUrl);
      if (memoryCached) {
        const age = Date.now() - memoryCached.timestamp;
        logCacheHit('stations:fetch', 'memory-cache', age, memoryCached.data.scenario_count);
        Logger.debug(`Using in-memory cached station manifest for ${manifestUrl}`, LOG_TAG);
        return { data: memoryCached.data, source: 'cache', isStale: false, cacheAge: age };
      }
    }

    // Tier 2: localStorage cache
    if (!forceRefresh) {
      const localCached = AtcManifestCacheService.getCachedStationManifest(manifestUrl);
      if (localCached) {
        const age = Date.now() - localCached.timestamp;
        const cacheStatus = AtcManifestCacheService.getStationManifestCacheStatus(manifestUrl);

        // Promote into memory tier
        AtcManifestMemoryCache.setStationManifest(manifestUrl, localCached.data, localCached.timestamp);

        logCacheHit('stations:fetch', 'localStorage-cache', age, localCached.data.scenario_count);
        Logger.debug(`Using localStorage cached station manifest for ${manifestUrl} (stale: ${cacheStatus.isStale})`, LOG_TAG);

        // SWR: if stale, schedule background refresh
        if (cacheStatus.isStale) {
          Logger.info(`localStorage cache for ${manifestUrl} is stale, triggering deferred background refresh`, LOG_TAG);
          AtcManifestSwrController.scheduleStationManifestRefresh(
            manifestUrl,
            circuitBreaker.isOpen(),
            () => this.performStationManifestRefresh(manifestUrl),
          );
        }

        return { data: localCached.data, source: 'cache', isStale: cacheStatus.isStale, cacheAge: age };
      }
    }

    // Tier 3: Network fetch
    return this.fetchAndCacheStationManifest(manifestUrl);
  }

  // ── Convenience methods ──────────────────────────────────────

  /**
   * Get all available stations (convenience method).
   * Uses mappers to transform API data to domain entities.
   */
  static async getAvailableStations(forceRefresh = false): Promise<AtcStation[]> {
    const mainManifest = await this.fetchMainManifest(forceRefresh);
    return mapApiMainManifestToStations(mainManifest as Parameters<typeof mapApiMainManifestToStations>[0]);
  }

  /**
   * Load scenarios for a specific station.
   * Uses mappers to transform API data and merge with existing station.
   */
  static async loadStationScenarios(station: AtcStation, forceRefresh = false): Promise<AtcStation> {
    const stationManifest = await this.fetchStationManifest(
      { manifestUrl: station.manifestUrl },
      forceRefresh,
    );
    const mappedManifest = mapApiStationManifestToInternal(
      stationManifest as Parameters<typeof mapApiStationManifestToInternal>[0],
    );
    return mergeStationWithManifest(station, mappedManifest);
  }

  /**
   * Get a random scenario URL from a station.
   */
  static async getRandomScenarioUrl(station: AtcStation): Promise<string | null> {
    let stationWithScenarios = station;

    if (!station.scenarios || station.scenarios.length === 0) {
      stationWithScenarios = await this.loadStationScenarios(station);
    }

    if (!stationWithScenarios.scenarios || stationWithScenarios.scenarios.length === 0) {
      return null;
    }

    const randomIndex = Math.floor(Math.random() * stationWithScenarios.scenarios.length);
    return stationWithScenarios.scenarios[randomIndex].cdn_url;
  }

  // ── Cache management ─────────────────────────────────────────

  /**
   * Clear all cached manifests (in-memory and localStorage)
   */
  static clearCache(): void {
    AtcManifestMemoryCache.clearAll();
    AtcManifestSwrController.resetInFlightStatus();
    AtcManifestCacheService.clearAllCache();
    Logger.info('Cleared all manifest caches (memory + localStorage)', LOG_TAG);
  }

  /**
   * Reset in-flight status (useful for testing)
   */
  static resetInFlightStatus(): void {
    AtcManifestSwrController.resetInFlightStatus();
  }

  /**
   * Clear cache for a specific station (in-memory and localStorage)
   */
  static clearStationCache(manifestUrl: string): void {
    AtcManifestMemoryCache.clearStation(manifestUrl);
    AtcManifestCacheService.clearStationManifestCache(manifestUrl);
    Logger.info(`Cleared cache for station: ${manifestUrl}`, LOG_TAG);
  }

  // ── Private: network fetch + cache write ─────────────────────

  /**
   * Fetch main manifest from network and write to both cache tiers.
   */
  private static async fetchAndCacheMainManifest(): Promise<AtcManifestResult<AtcMainManifest>> {
    const result = await fetchMainManifestFromNetwork();

    // Write to memory tier
    AtcManifestMemoryCache.setMainManifest(result.data);

    // Write to localStorage tier
    AtcManifestCacheService.setCachedMainManifest(result.data, result.etag);

    return result;
  }

  /**
   * Fetch station manifest from network and write to both cache tiers.
   */
  private static async fetchAndCacheStationManifest(
    manifestUrl: string,
  ): Promise<AtcManifestResult<AtcStationManifest>> {
    const result = await fetchStationManifestFromNetwork(manifestUrl);

    // Write to memory tier
    AtcManifestMemoryCache.setStationManifest(manifestUrl, result.data);

    // Write to localStorage tier
    AtcManifestCacheService.setCachedStationManifest(manifestUrl, result.data, result.etag);

    return result;
  }

  // ── Private: SWR refresh callbacks ───────────────────────────

  /**
   * Perform main manifest background refresh and notify subscribers.
   */
  private static async performMainManifestRefresh(): Promise<void> {
    const result = await this.fetchAndCacheMainManifest();
    const stations = mapApiMainManifestToStations(
      result.data as Parameters<typeof mapApiMainManifestToStations>[0],
    );
    Logger.info(`Background refresh completed: ${stations.length} stations`, LOG_TAG);
    AtcManifestSwrController.notifyBackgroundRefresh(stations);
  }

  /**
   * Perform station manifest background refresh.
   */
  private static async performStationManifestRefresh(manifestUrl: string): Promise<void> {
    await this.fetchAndCacheStationManifest(manifestUrl);
  }
}

// ── Instance wrapper (backward-compatible) ───────────────────────

export class DefaultAtcManifestService implements AtcManifestServiceApi {
  fetchMainManifest(forceRefresh = false): Promise<AtcMainManifest> {
    return AtcManifestService.fetchMainManifest(forceRefresh);
  }

  fetchMainManifestWithSource(forceRefresh = false): Promise<AtcManifestResult<AtcMainManifest>> {
    return AtcManifestService.fetchMainManifestWithSource(forceRefresh);
  }

  fetchStationManifest(
    stationInfo: AtcStationInfo | { manifestUrl: string; airportCode?: string; stationType?: string },
    forceRefresh = false,
  ): Promise<AtcStationManifest> {
    return AtcManifestService.fetchStationManifest(stationInfo, forceRefresh);
  }

  fetchStationManifestWithSource(
    stationInfo: AtcStationInfo | { manifestUrl: string; airportCode?: string; stationType?: string },
    forceRefresh = false,
  ): Promise<AtcManifestResult<AtcStationManifest>> {
    return AtcManifestService.fetchStationManifestWithSource(stationInfo, forceRefresh);
  }

  getAvailableStations(forceRefresh = false): Promise<AtcStation[]> {
    return AtcManifestService.getAvailableStations(forceRefresh);
  }

  loadStationScenarios(station: AtcStation, forceRefresh = false): Promise<AtcStation> {
    return AtcManifestService.loadStationScenarios(station, forceRefresh);
  }

  getRandomScenarioUrl(station: AtcStation): Promise<string | null> {
    return AtcManifestService.getRandomScenarioUrl(station);
  }

  clearCache(): void {
    AtcManifestService.clearCache();
  }

  clearStationCache(manifestUrl: string): void {
    AtcManifestService.clearStationCache(manifestUrl);
  }
}

const defaultAtcManifestService = new DefaultAtcManifestService();

export const getAtcManifestService = (): AtcManifestServiceApi => defaultAtcManifestService;
